<script setup lang="ts">
import type { CategorySlice } from '@/lib/charts/types'
import type { FleetTelemetry, HealthState } from '@/types'
import { Link } from '@inertiajs/vue3'
import { computed } from 'vue'
import DonutChart from '@/components/charts/DonutChart.vue'
import HealthBadge from '@/components/telemetry/HealthBadge.vue'
import { formatRelative, healthPresentation } from '@/lib/health'
import { adminInstancePath } from '@/routes'

const props = defineProps<{
  telemetry: FleetTelemetry
}>()

const summaryStats = computed(() => [
  { label: 'Instances', value: props.telemetry.total, icon: 'i-lucide-server' },
  { label: 'Reporting', value: props.telemetry.reporting, icon: 'i-lucide-satellite-dish' },
  { label: 'Down', value: props.telemetry.down, icon: 'i-lucide-circle-x' },
  { label: 'Never reported', value: props.telemetry.never_reported, icon: 'i-lucide-circle-help' },
])

const healthSegments = computed<CategorySlice[]>(() =>
  (Object.entries(props.telemetry.health_distribution) as [HealthState, number][])
    .filter(([, count]) => count > 0)
    .map(([state, count]) => {
      const presentation = healthPresentation(state)
      return { id: state, label: presentation.label, value: count, color: presentation.chartColor }
    }),
)

const maxVersionCount = computed(() =>
  Math.max(1, ...props.telemetry.version_adoption.map(entry => entry.count)),
)

function versionShare(count: number): number {
  return props.telemetry.total === 0 ? 0 : Math.round((count / props.telemetry.total) * 100)
}
</script>

<template>
  <div v-if="telemetry.total === 0" class="ring-default text-muted rounded-lg p-10 text-center text-sm ring">
    <UIcon name="i-lucide-satellite-dish" class="mx-auto mb-2 size-6" />
    No instances to report on yet.
  </div>

  <div v-else class="flex flex-col gap-8">
    <!-- Summary -->
    <div class="grid grid-cols-2 gap-4 lg:grid-cols-4">
      <div v-for="stat in summaryStats" :key="stat.label" class="ring-default rounded-lg p-4 ring">
        <div class="text-muted flex items-center gap-2 text-xs uppercase tracking-wide">
          <UIcon :name="stat.icon" class="size-4" />
          {{ stat.label }}
        </div>
        <p class="text-highlighted mt-1 text-2xl font-semibold tabular-nums">
          {{ stat.value }}
        </p>
      </div>
    </div>

    <div class="grid gap-8 lg:grid-cols-2">
      <!-- Health distribution -->
      <section class="flex flex-col gap-4">
        <div>
          <h3 class="text-highlighted font-semibold">
            Health distribution
          </h3>
          <p class="text-muted text-sm">
            Current state across all instances.
          </p>
        </div>
        <div class="ring-default flex flex-wrap items-center gap-6 rounded-lg p-5 ring">
          <DonutChart
            :slices="healthSegments"
            :central-label="String(telemetry.total)"
            central-sub-label="total"
          />
          <ul class="flex min-w-40 flex-1 flex-col gap-2">
            <li v-for="segment in healthSegments" :key="segment.id" class="flex items-center justify-between gap-3">
              <span class="flex items-center gap-2 text-sm">
                <span class="size-2.5 rounded-full" :style="{ backgroundColor: segment.color }" />
                <span class="text-toned">{{ segment.label }}</span>
              </span>
              <span class="text-highlighted text-sm font-medium tabular-nums">{{ segment.value }}</span>
            </li>
          </ul>
        </div>
      </section>

      <!-- Version adoption -->
      <section class="flex flex-col gap-4">
        <div>
          <h3 class="text-highlighted font-semibold">
            Version adoption
          </h3>
          <p class="text-muted text-sm">
            How the fleet is distributed across reported versions.
          </p>
        </div>
        <div class="ring-default flex flex-col gap-3 rounded-lg p-5 ring">
          <div v-for="entry in telemetry.version_adoption" :key="entry.version" class="flex flex-col gap-1">
            <div class="flex items-center justify-between gap-2 text-sm">
              <span class="text-toned font-mono">{{ entry.version }}</span>
              <span class="text-muted tabular-nums">{{ entry.count }} · {{ versionShare(entry.count) }}%</span>
            </div>
            <div class="bg-elevated h-2 overflow-hidden rounded-full">
              <div
                class="bg-primary h-full rounded-full"
                :style="{ width: `${(entry.count / maxVersionCount) * 100}%` }"
              />
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- Instances -->
    <section class="flex flex-col gap-4">
      <h3 class="text-highlighted font-semibold">
        Instances
      </h3>
      <div class="divide-default ring-default divide-y rounded-lg ring">
        <Link
          v-for="instance in telemetry.deployments"
          :key="instance.id"
          :href="adminInstancePath(instance.id)"
          class="hover:bg-elevated/50 flex items-center justify-between gap-3 px-4 py-3 transition"
        >
          <div class="flex items-center gap-3">
            <HealthBadge :state="instance.state" />
            <span class="text-highlighted font-medium">{{ instance.customer.name }}</span>
          </div>
          <div class="flex items-center gap-4">
            <span class="text-toned font-mono text-sm">{{ instance.version || '—' }}</span>
            <span class="text-dimmed text-xs whitespace-nowrap">{{ formatRelative(instance.last_seen) }}</span>
            <UIcon name="i-lucide-chevron-right" class="text-dimmed size-4" />
          </div>
        </Link>
      </div>
    </section>
  </div>
</template>
